import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import LockResetRounded from "@mui/icons-material/LockResetRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { resetUserTwoFactor } from "../utils/api";

/**
 * Сброс второго фактора администратором.
 *
 * Ручка принимает числовой id пользователя, а списка пользователей бэкенд не
 * отдаёт (есть только POST /auth/users для создания), поэтому id вводится
 * вручную. Действие необратимо и снимает защиту, так что оно за подтверждением.
 */
const TwoFactorAdminReset: React.FC<{
  onNotify?: (message: string, severity: "success" | "error") => void;
}> = ({ onNotify }) => {
  const theme = useTheme();
  const [userId, setUserId] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedId = Number(userId);
  const valid = userId.trim() !== "" && Number.isInteger(parsedId) && parsedId > 0;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await resetUserTwoFactor(parsedId);
      onNotify?.(
        `Второй фактор снят с пользователя #${parsedId}`,
        "success",
      );
      setOpen(false);
      setUserId("");
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail?.message || "Не удалось сбросить второй фактор";
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: "20px", mb: 2.5 }}>
      <Stack direction="row" spacing={1.75} alignItems="center" mb={2.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: "13px",
            color: theme.palette.semanticText.warning,
            bgcolor: alpha(theme.palette.warning.main, 0.11),
          }}
        >
          <LockResetRounded fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontSize: 17 }}>
            Сброс второго фактора
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            Для пользователей, потерявших доступ к приложению
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="flex-start">
        <TextField
          label="ID пользователя"
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))}
          size="small"
          sx={{ maxWidth: 200 }}
          slotProps={{ htmlInput: { inputMode: "numeric" } }}
          helperText="Числовой id учётной записи"
        />
        <Button
          variant="outlined"
          color="warning"
          disabled={!valid}
          onClick={() => setOpen(true)}
          sx={{ mt: { sm: 0.25 } }}
        >
          Сбросить
        </Button>
      </Stack>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Снять второй фактор?</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <WarningAmberRounded color="warning" fontSize="small" />
            <Typography sx={{ fontSize: 14 }}>
              Пользователь <b>#{parsedId}</b> сможет войти по одному паролю, пока
              не настроит второй фактор заново. Убедитесь, что запрос пришёл
              действительно от владельца учётной записи.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirm}
            disabled={busy}
          >
            {busy ? <CircularProgress size={20} color="inherit" /> : "Сбросить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TwoFactorAdminReset;
